import React, { useState } from 'react';
import { useOnboarding } from './ImportJobContext';
import { IS_MOCK_API } from './onboarding.service';
import { ONBOARDING_LEAN } from './flags';
import { salonService } from '../../_services/salon.service';
import { hairdresserService } from '../../_services/hairdresser.service';
import { IconTick, IconWarn } from './icons';

/**
 * Step 3: Reveal (plan §4.4). "This is your Tuesday." A real day with the
 * imported bookings, a four-row receipt with View links, then the go-live
 * choice. Both CTAs close the layer onto the diary and mark onboarding done.
 */

const fmt = (n) => (n || 0).toLocaleString('en-GB');
const toMin = (hhmm) => { const [h, m] = String(hhmm || '0:0').split(':').map(Number); return h * 60 + (m || 0); };

function DayPreview({ day }) {
  if (!day || !day.bookings?.length) return null;
  const starts = day.bookings.map((b) => toMin(b.start));
  const ends = day.bookings.map((b) => toMin(b.end || b.start));
  const first = Math.floor(Math.min(...starts) / 60) * 60;
  const last = Math.ceil(Math.max(...ends) / 60) * 60;
  const hours = [];
  for (let t = first; t <= last; t += 60) hours.push(t);
  const H = 38; // px per hour (matches .ob-day-time)
  const label = day.label || day.date;
  return (
    <div className="ob-day" aria-label={`Preview of ${label}`}>
      <div className="ob-day-head"><b>{label}</b><span>{day.bookings.length} booked</span></div>
      <div className="ob-day-grid" style={{ height: (hours.length - 1) * H + 8 }}>
        <div className="ob-day-times">
          {hours.slice(0, -1).map((t) => <div className="ob-day-time" key={t}>{String(Math.floor(t / 60)).padStart(2, '0')}:00</div>)}
        </div>
        <div className="ob-day-col">
          {day.bookings.map((b, i) => {
            const top = ((toMin(b.start) - first) / 60) * H;
            const h = Math.max(((toMin(b.end || b.start) - toMin(b.start)) / 60) * H - 4, 26);
            return (
              <div className={`ob-day-block ${b.tone === 'lilac' ? 'ob-day-block--lilac' : ''}`} style={{ top, height: h, animationDelay: `${i * 90}ms` }} key={`${b.start}-${i}`}>
                <b>{b.clientName}, {b.serviceName}</b>
                <span>{b.start}{b.end ? `-${b.end}` : ''}{b.staffName ? ` · ${b.staffName}` : ''}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function RevealScreen({ onDone, salonId }) {
  const { job, patchOnboarding } = useOnboarding();
  const s = job?.summary || {};
  const answers = job?.answers || {};
  const staffItem = (job?.queue || []).find((q) => q.type === 'staff_review');
  const staffAnswer = staffItem ? answers[staffItem.id] : null;
  const invitedNames = staffAnswer?.invited?.length ? staffAnswer.invited.map((i) => (i.name || '').split(' ')[0]).filter(Boolean) : [];
  const foundNames = (staffItem?.staff || []).map((p) => p.name.split(' ')[0]);
  const marketplaceItem = (job?.queue || []).find((q) => q.type === 'decision' && q.key === 'marketplace');
  const wantsMarketplace = marketplaceItem ? answers[marketplaceItem.id]?.value === true : false;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [toast, setToast] = useState('');

  const names = (list) => (list.length <= 1 ? list[0] || '' : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`);

  // Lean flow: the reveal hands over to the guided first tasks on the diary.
  // Going live is its own screen after those. The old direct switch stays
  // for the non-lean path.
  const showAround = async () => {
    setBusy(true);
    await patchOnboarding({ status: 'in_progress', checklist: { import: 'done', questions: 'done' } });
    onDone('tour');
  };

  const finish = async (live) => {
    setBusy(true); setErr('');
    try {
      if (live && !IS_MOCK_API) {
        const fd = new FormData();
        fd.append('salonId', localStorage.getItem('salonId') || '');
        fd.append('onlineBookingEnabled', true);
        await salonService.updateSalon(fd);
        if (wantsMarketplace) await hairdresserService.updateProfileV2({ showOnMarketplace: true });
      }
      await patchOnboarding({ status: 'completed', checklist: { live: live ? 'done' : 'later', questions: 'done' } });
      if (live) setToast('Your booking page is live');
      onDone(live ? 'live' : 'private');
    } catch (e) {
      setErr(e?.response?.data?.messages || e.message || 'Could not switch bookings on. You can do it from Settings.');
      setBusy(false);
    }
  };

  const bookingsLabel = s.bookings ? `${fmt(s.bookings)} bookings${job?.previewDay ? ', next weeks in' : ''}` : null;

  return (
    <div className="ob-screen">
      <div className="ob-eyebrow-row"><span className="ob-eyebrow">Ready</span></div>
      <h2 className="ob-h1">{job?.previewDay?.label ? `This is your ${job.previewDay.label.split(' ')[0]}.` : 'This is your diary.'}</h2>
      <div className="ob-screen-scroll">
        <DayPreview day={job?.previewDay} />
        <div className="ob-receipt">
          {s.clients > 0 && <div className="ob-receipt-row" style={{ animationDelay: '60ms' }}><IconTick /><span>{fmt(s.clients)} clients added</span><a href="/clients">View</a></div>}
          {s.services > 0 && <div className="ob-receipt-row" style={{ animationDelay: '120ms' }}><IconTick /><span>{fmt(s.services)} services at your prices</span><a href="/services">View</a></div>}
          {s.products > 0 && <div className="ob-receipt-row" style={{ animationDelay: '160ms' }}><IconTick /><span>{fmt(s.products)} products on the shelf</span><a href="/products">View</a></div>}
          {bookingsLabel && <div className="ob-receipt-row" style={{ animationDelay: '200ms' }}><IconTick /><span>{bookingsLabel}</span><a href={`/diary/${salonId || localStorage.getItem('salonId')}`}>View</a></div>}
          {foundNames.length > 0 && (invitedNames.length
            ? <div className="ob-receipt-row" style={{ animationDelay: '240ms' }}><IconTick /><span>Invites sent to {names(invitedNames)}</span><a href="/staff">View</a></div>
            : <div className="ob-receipt-row ob-receipt-row--warn" style={{ animationDelay: '240ms' }}><IconWarn /><span>{names(foundNames)} found, not yet invited</span><a href="/staff">Invite</a></div>)}
        </div>
        {err && <div className="ob-error" role="alert">{err}</div>}
      </div>
      {ONBOARDING_LEAN ? (
        <div className="ob-reveal-ctas">
          <button type="button" className="ob-btn ob-btn--primary" disabled={busy} onClick={showAround}>{busy ? 'One moment…' : 'Show me around'}</button>
          <button type="button" className="ob-btn ob-btn--ghost" disabled={busy} onClick={() => onDone('live')}>Skip to going live</button>
        </div>
      ) : (
        <div className="ob-reveal-ctas">
          <button type="button" className="ob-btn ob-btn--primary" disabled={busy} onClick={() => finish(true)}>{busy ? 'One moment…' : 'Take my bookings live'}</button>
          <button type="button" className="ob-btn ob-btn--secondary" disabled={busy} onClick={() => finish(false)}>Keep it private for now</button>
        </div>
      )}
      {toast && <div role="status" style={{ position: 'absolute', left: 0, right: 0, bottom: 12, textAlign: 'center', fontSize: 12 }}>{toast}</div>}
    </div>
  );
}
